import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import { AlertCircle, ChevronDown, Copy, Download, Edit, Loader, MapPin, Plus, Printer, RotateCcw, Search, Users, X } from "lucide-react";
import Swal from "sweetalert2";
import { jsPDF } from "jspdf";
import { API_BASE_URL } from "../../../../../api";
import logoImg from "../../../../images/logo1_aveiro.png";
import { getDirectReportsRequest } from "../../../../redux/actions/userActions";
import {
    createMissionRequestForCollabRequest,
    duplicateMissionRequestRequest,
    getMyCreatedMissionRequestsRequest,
} from "../../../../redux/actions/missionRequestActions";
import MissionInlineForm, { calculateDurationDays } from "./MissionInlineForm";
import "../../parametres/parametresStyles/ParamConges.css";
import "../../Offboarding/OffboardingStyles/Offboarding.css";
import "../MesrequetesStyles/DemandeConges.css";

const CREATED_AS_MANAGER = "MANAGER";

const SearchableCombobox = ({ value, onChange, options, placeholder }) => {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState(value || "");

    useEffect(() => { setQuery(value || ""); }, [value]);

    const filteredOptions = useMemo(() => {
        if (!query) return options;
        return options.filter((option) => option.label.toLowerCase().includes(query.toLowerCase()));
    }, [options, query]);

    const selectOption = (option) => {
        setQuery(option.label);
        onChange(option.value);
        setOpen(false);
    };

    return <div style={{ position: "relative", width: "100%" }}>
        <div style={{ position: "relative" }}>
            <input className="pcInput" type="text" style={{ width: "100%", fontSize: 13, paddingRight: 56 }} placeholder={placeholder} value={query}
                onChange={(event) => { setQuery(event.target.value); onChange(event.target.value); setOpen(true); }} onFocus={() => setOpen(true)} />
            <div style={{ position: "absolute", right: 6, top: "50%", transform: "translateY(-50%)", display: "flex", alignItems: "center", gap: 2 }}>
                {query && <button type="button" onClick={() => { setQuery(""); onChange(""); setOpen(false); }} title="Effacer" style={{ border: "none", background: "none", cursor: "pointer", color: "#9ca3af", padding: 4, lineHeight: 0 }}><X size={13} /></button>}
                <button type="button" onClick={() => setOpen((isOpen) => !isOpen)} title="Afficher la liste" style={{ border: "none", background: "none", cursor: "pointer", color: "#9ca3af", padding: 4, lineHeight: 0 }}><ChevronDown size={14} /></button>
            </div>
        </div>
        {open && filteredOptions.length > 0 && <div style={{ position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, zIndex: 30, maxHeight: 220, overflowY: "auto", background: "#fff", border: "1px solid #e2e8f0", borderRadius: 8, boxShadow: "0 4px 12px rgba(0,0,0,.10)" }}>
            {filteredOptions.map((option) => <button key={option.value} type="button" onClick={() => selectOption(option)} style={{ display: "block", width: "100%", padding: "8px 12px", border: "none", background: option.value === value ? "#eff6ff" : "#fff", color: option.value === value ? "#204385" : "#374151", fontSize: 13, fontWeight: option.value === value ? 600 : 400, textAlign: "left", cursor: "pointer" }}>{option.label}</button>)}
        </div>}
    </div>;
};

const DemandeMissionManager = () => {
    const dispatch = useDispatch();
    const user = useSelector((state) => state.auth.user);
    const { directReports, directReportsLoading } = useSelector((state) => state.user || {
        directReports: [],
        directReportsLoading: false,
    });
    const { loading, myCreatedRequests } = useSelector((state) => state.missionRequests || {
        loading: false,
        myCreatedRequests: [],
    });

    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDuplicateModal, setShowDuplicateModal] = useState(false);
    const [selectedDemande, setSelectedDemande] = useState(null);
    const [searchCollaborateur, setSearchCollaborateur] = useState("");
    const [selectedCollaborateurs, setSelectedCollaborateurs] = useState([]);
    const [filterCollab, setFilterCollab] = useState("");
    const [filterStatut, setFilterStatut] = useState("");
    const [filterLieu, setFilterLieu] = useState("");
    const [filterDateOp, setFilterDateOp] = useState("");

    const headers = useMemo(() => ({
        uid: sessionStorage.getItem("uid"),
        Authorization: `Bearer ${sessionStorage.getItem("token")}`,
        "X-Tenant-Id": sessionStorage.getItem("selectedCompanyId"),
        "Content-Type": "application/json",
        Accept: "application/json",
    }), []);

    useEffect(() => {
        if (user?.id) dispatch(getDirectReportsRequest(user.id));
        dispatch(getMyCreatedMissionRequestsRequest(CREATED_AS_MANAGER));
    }, [dispatch, user?.id]);

    const collaborateursFiltres = useMemo(() => {
        if (!searchCollaborateur.trim()) return directReports || [];
        const term = searchCollaborateur.toLowerCase();
        return (directReports || []).filter((c) =>
            c.firstName?.toLowerCase().includes(term) ||
            c.lastName?.toLowerCase().includes(term) ||
            c.employeeCode?.toLowerCase().includes(term) ||
            c.email?.toLowerCase().includes(term)
        );
    }, [directReports, searchCollaborateur]);

    const toggleCollaborateur = (collabId) => {
        setSelectedCollaborateurs((prev) =>
            prev.includes(collabId) ? prev.filter((id) => id !== collabId) : [...prev, collabId]
        );
    };

    const handleSubmit = (payload) => {
        if (selectedCollaborateurs.length === 0) {
            Swal.fire({
                icon: "error",
                title: "Erreur",
                text: "Veuillez selectionner au moins un collaborateur",
                confirmButtonColor: "#204385",
            });
            return;
        }

        dispatch(createMissionRequestForCollabRequest({
            ...payload,
            collaborateurIds: selectedCollaborateurs,
            createdAsRole: CREATED_AS_MANAGER,
            onSuccess: () => {
                setSelectedCollaborateurs([]);
                setSearchCollaborateur("");
                setShowCreateModal(false);
                dispatch(getMyCreatedMissionRequestsRequest(CREATED_AS_MANAGER));
            },
        }));
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return "-";
        return new Date(dateStr).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" });
    };

    const renderLocation = (demande) => {
        const parts = [demande.ordreMissionPays, demande.ordreMissionVille, demande.ordreMissionAdresse].filter(Boolean);
        return parts.length > 0 ? parts.join(", ") : demande.ordreMissionLieu;
    };

    const handleDuplicate = (demande) => {
        setSelectedDemande(demande);
        setShowDuplicateModal(true);
    };

    const handleDuplicateSubmit = (payload) => {
        if (!selectedDemande) return;
        dispatch(createMissionRequestForCollabRequest({
            ...payload,
            collaborateurIds: [selectedDemande.employeeId],
        }));
        setShowDuplicateModal(false);
        setSelectedDemande(null);
        dispatch(getMyCreatedMissionRequestsRequest(CREATED_AS_MANAGER));
        Swal.fire({
            icon: "success",
            title: "Succès",
            text: "Ordre de mission dupliqué",
            timer: 1600,
            showConfirmButton: false,
        });
    };

    const canEditDemande = (demande) =>
        demande && !["APPROVED", "REJECTED", "CANCELLED"].includes(demande.status)
        && (demande.status === "PENDING_ADMIN" || demande.status === "PENDING_MANAGER");

    const canCancelDemande = canEditDemande;

    const handleCancel = (demande) => {
        if (!demande) return;
        Swal.fire({
            title: "Confirmation",
            text: "Voulez-vous vraiment annuler cette demande ?",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#ef4444",
            cancelButtonColor: "#6b7280",
            confirmButtonText: "Oui, annuler",
            cancelButtonText: "Non",
        }).then(async (result) => {
            if (!result.isConfirmed) return;
            try {
                await axios.post(`${API_BASE_URL}/api/mission-requests/requests/${demande.id}/cancel`, null, { headers });
                await Swal.fire({
                    icon: "success",
                    title: "Succès",
                    text: "Demande annulée avec succès",
                    timer: 1600,
                    showConfirmButton: false,
                });
                dispatch(getMyCreatedMissionRequestsRequest(CREATED_AS_MANAGER));
            } catch (error) {
                const message = error?.response?.data?.message || error?.response?.data?.error || error?.message || "Erreur annulation demande";
                Swal.fire({ icon: "error", title: "Erreur", text: message, confirmButtonColor: "#d33" });
            }
        });
    };

    const buildInitialMission = (demande) => ({
        natureMissionId: demande.natureMissionId || "",
        code: demande.ordreMissionCode || "",
        libelle: demande.ordreMissionLibelle || "",
        description: demande.ordreMissionDescription || "",
        objetMission: demande.ordreMissionObjet || "",
        dateDebut: demande.ordreMissionDateDebut || "",
        dateFin: demande.ordreMissionDateFin || "",
        zoneMission: demande.ordreMissionZoneMission || "EN_VILLE",
        pays: demande.ordreMissionPays || "",
        ville: demande.ordreMissionVille || "",
        adresse: demande.ordreMissionAdresse || "",
        engagementsCollaborateur: demande.ordreMissionEngagementsCollaborateur || "",
        workflow: demande.workflowLevel || "both",
    });

    const openEdit = (demande) => {
        setSelectedDemande(demande);
        setShowEditModal(true);
    };

    const handleEditSubmit = async (payload) => {
        if (!selectedDemande) return;
        try {
            await axios.put(
                `${API_BASE_URL}/api/mission-requests/requests/${selectedDemande.id}/ordre-mission`,
                payload.ordreMission,
                { headers }
            );
            setShowEditModal(false);
            setSelectedDemande(null);
            dispatch(getMyCreatedMissionRequestsRequest(CREATED_AS_MANAGER));
            Swal.fire({
                icon: "success",
                title: "Succès",
                text: "Ordre de mission modifié",
                timer: 1600,
                showConfirmButton: false,
            });
        } catch (error) {
            const message = error?.response?.data?.message || error?.response?.data?.error || error?.message || "Erreur modification ordre de mission";
            Swal.fire({ icon: "error", title: "Erreur", text: message, confirmButtonColor: "#d33" });
        }
    };

    const matchesCreatedFilters = (demande, { skipCollab, skipStatut, skipLieu, skipDate } = {}) => {
        if (!skipCollab && filterCollab) {
            const collaborator = `${demande.employeeName || ""} ${demande.employeeMatricule || ""}`.toLowerCase();
            if (!collaborator.includes(filterCollab.toLowerCase())) return false;
        }
        if (!skipStatut && filterStatut && (demande.status || "").toLowerCase() !== filterStatut.toLowerCase()) return false;
        if (!skipLieu && filterLieu && !(renderLocation(demande) || "").toLowerCase().includes(filterLieu.toLowerCase())) return false;
        if (!skipDate && filterDateOp) {
            const start = demande.ordreMissionDateDebut ? new Date(demande.ordreMissionDateDebut) : null;
            const end = demande.ordreMissionDateFin ? new Date(demande.ordreMissionDateFin) : start;
            const selectedDate = new Date(`${filterDateOp}T00:00:00`);
            if (!start || Number.isNaN(selectedDate.getTime()) || selectedDate < new Date(start.toDateString()) || selectedDate > new Date(end.toDateString())) return false;
        }
        return true;
    };

    const handleDownloadPdf = async (demande, print = false) => {
        try {
            const doc = new jsPDF();
            const image = await new Promise((resolve, reject) => {
                const img = new Image();
                img.src = logoImg;
                img.onload = () => resolve(img);
                img.onerror = reject;
            });
            doc.addImage(image, "PNG", 20, 10, 23, 23);
            doc.setFont("helvetica", "bold");
            doc.setFontSize(14);
            doc.setTextColor(42, 92, 169);
            doc.text("ORDRE DE MISSION", 20, 42);
            doc.setFont("helvetica", "normal");
            doc.text(`Ref. ${demande.ordreMissionCode || "N/A"}`, 20, 50);

            let y = 65;
            const addField = (label, value) => {
                doc.setFont("helvetica", "bold");
                doc.setFontSize(10);
                const labelText = `${label} : `;
                doc.text(labelText, 20, y);
                doc.setFont("helvetica", "normal");
                doc.text(String(value || "-"), 20 + doc.getTextWidth(labelText), y);
                y += 7;
            };
            const addSection = (title) => {
                doc.setFont("helvetica", "bold");
                doc.setFontSize(12);
                doc.setTextColor(42, 92, 169);
                doc.text(title, 20, y);
                y += 8;
                doc.setTextColor(0, 0, 0);
            };
            addSection("1. Identification du collaborateur");
            addField("Nom et prénom", demande.employeeName || "Collaborateur");
            addField("Fonction", demande.employeeJobName);
            addField("CIN / Passeport", demande.employeeCin || demande.employeePasseport);
            y += 4;
            addSection("2. Objet de la mission");
            doc.setFont("helvetica", "normal");
            doc.setFontSize(10);
            const objet = `${demande.ordreMissionLibelle || ""}${demande.ordreMissionObjet ? ` - ${demande.ordreMissionObjet}` : ""}`;
            const objetLines = doc.splitTextToSize(objet || "-", 170);
            doc.text(objetLines, 20, y);
            y += objetLines.length * 5 + 6;
            addSection("3. Lieu et durée de la mission");
            const lieu = [demande.ordreMissionPays, demande.ordreMissionVille, demande.ordreMissionAdresse].filter(Boolean).join(", ") || demande.ordreMissionLieu;
            addField("Lieu de la mission", lieu);
            addField("Date de départ", formatDate(demande.ordreMissionDateDebut));
            addField("Date de retour", formatDate(demande.ordreMissionDateFin));
            const duration = calculateDurationDays(demande.ordreMissionDateDebut, demande.ordreMissionDateFin);
            addField("Durée totale", duration ? `${duration} jour${duration > 1 ? "s" : ""}` : "-");
            doc.setFont("helvetica", "normal");
            doc.setFontSize(11);
            doc.text(`Fait à Agadir, le ${new Date(demande.createdAt || Date.now()).toLocaleDateString("fr-FR")}`, 105, y + 10);

            if (print) {
                const printFrame = document.createElement("iframe");
                const blobUrl = URL.createObjectURL(doc.output("blob"));
                printFrame.style.display = "none";
                printFrame.src = blobUrl;
                printFrame.onload = () => {
                    printFrame.contentWindow?.print();
                    setTimeout(() => { printFrame.remove(); URL.revokeObjectURL(blobUrl); }, 1000);
                };
                document.body.appendChild(printFrame);
                return;
            }
            doc.save(`Ordre_Mission_${demande.ordreMissionCode || demande.id}.pdf`);
        } catch (error) {
            console.error("PDF generation error", error);
            Swal.fire({ icon: "error", title: "Erreur", text: "Une erreur est survenue lors de la génération du PDF.", confirmButtonColor: "#ef4444" });
        }
    };

    const collabOptions = useMemo(() => {
        const collaborators = new Map();
        (myCreatedRequests || []).filter((request) => matchesCreatedFilters(request, { skipCollab: true })).forEach((request) => {
            if (request.employeeName && !collaborators.has(request.employeeName)) collaborators.set(request.employeeName, { value: request.employeeName, label: request.employeeMatricule ? `${request.employeeName} (${request.employeeMatricule})` : request.employeeName });
        });
        return [...collaborators.values()].sort((left, right) => left.label.localeCompare(right.label));
    }, [myCreatedRequests, filterStatut, filterLieu, filterDateOp]);
    const statutOptions = useMemo(() => [...new Set((myCreatedRequests || []).filter((request) => matchesCreatedFilters(request, { skipStatut: true })).map((request) => request.status).filter(Boolean))].sort().map((status) => ({ value: status, label: status })), [myCreatedRequests, filterCollab, filterLieu, filterDateOp]);
    const lieuOptions = useMemo(() => [...new Set((myCreatedRequests || []).filter((request) => matchesCreatedFilters(request, { skipLieu: true })).map(renderLocation).filter(Boolean))].sort().map((location) => ({ value: location, label: location })), [myCreatedRequests, filterCollab, filterStatut, filterDateOp]);
    const filteredCreatedRequests = useMemo(() => (myCreatedRequests || []).filter((request) => matchesCreatedFilters(request)), [myCreatedRequests, filterCollab, filterStatut, filterLieu, filterDateOp]);
    const resetCreatedFilters = () => { setFilterCollab(""); setFilterStatut(""); setFilterLieu(""); setFilterDateOp(""); };

    const renderCreatedRequests = () => (
        <div className="obCard obCard--noPad">
            <div className="obCard__body">
                <div className="obControls">
                    <div>
                        <h2 className="obCard__title">Ordres de mission créés</h2>
                        <div className="obMuted">
                            Demandes créées par vous pour vos collaborateurs.
                        </div>
                    </div>
                    <button className="obBtn obBtn--ghost" type="button" onClick={() => dispatch(getMyCreatedMissionRequestsRequest(CREATED_AS_MANAGER))} disabled={loading}>Actualiser</button>
                </div>

                <div style={{ display: "flex", flexWrap: "wrap", alignItems: "flex-end", gap: 16, marginBottom: 18, padding: 16, background: "#f8fafc", borderRadius: 10, border: "1px solid #e2e8f0" }}>
                    <div style={{ minWidth: 220, flex: "1 1 220px" }}><label style={{ fontSize: 12, fontWeight: 600, color: "#64748b", display: "block", marginBottom: 4 }}>Collaborateur / Matricule</label><SearchableCombobox value={filterCollab} onChange={setFilterCollab} options={collabOptions} placeholder="Nom ou matricule..." /></div>
                    <div style={{ minWidth: 180, flex: "1 1 180px" }}><label style={{ fontSize: 12, fontWeight: 600, color: "#64748b", display: "block", marginBottom: 4 }}>Lieu</label><SearchableCombobox value={filterLieu} onChange={setFilterLieu} options={lieuOptions} placeholder="Pays, ville..." /></div>
                    <div style={{ minWidth: 160, flex: "1 1 160px" }}><label style={{ fontSize: 12, fontWeight: 600, color: "#64748b", display: "block", marginBottom: 4 }}>Statut</label><SearchableCombobox value={filterStatut} onChange={setFilterStatut} options={statutOptions} placeholder="Rechercher un statut..." /></div>
                    <div style={{ minWidth: 160, flex: "1 1 160px" }}><label style={{ fontSize: 12, fontWeight: 600, color: "#64748b", display: "block", marginBottom: 4 }}>Date (période)</label><input type="date" className="pcInput" style={{ width: "100%", fontSize: 13 }} value={filterDateOp} onChange={(event) => setFilterDateOp(event.target.value)} /></div>
                    <button type="button" className="pcBtn pcBtn--ghost" style={{ padding: "10px 12px", display: "flex", alignItems: "center", gap: 6, fontSize: 13, height: 38, whiteSpace: "nowrap" }} onClick={resetCreatedFilters}><RotateCcw size={14} /> Réinitialiser</button>
                </div>

                <div className="obTableWrap">
                    <table className="obTable">
                        <thead>
                        <tr>
                            <th>Code</th>
                            <th>Collaborateur</th>
                            <th>Mission</th>
                            <th>Période</th>
                            <th>Lieu</th>
                            <th>Statut</th>
                            <th className="is-right">Actions</th>
                        </tr>
                        </thead>
                        <tbody>
                        {loading && (myCreatedRequests || []).length === 0 ? (
                            <tr><td colSpan={7} className="pcEmptyCell">Chargement...</td></tr>
                        ) : filteredCreatedRequests.length === 0 ? (
                            <tr><td colSpan={7} className="pcEmptyCell">Aucune demande créée</td></tr>
                        ) : filteredCreatedRequests.map((demande) => (
                            <tr key={demande.id}>
                                <td><span className="pcCode" style={{ background: "#eef2ff", border: "1px solid #c7d2fe", color: "#204385" }}>{demande.ordreMissionCode}</span></td>
                                <td>
                                    <div className="pcStrong">{demande.employeeName || "-"}</div>
                                    <div className="pcMuted" style={{ fontSize: 12 }}>{demande.employeeMatricule || ""}</div>
                                </td>
                                <td>
                                    <div className="pcStrong">{demande.ordreMissionLibelle}</div>
                                    <div className="pcMuted" style={{ fontSize: 12 }}>{demande.natureMissionLibelle || ""}</div>
                                </td>
                                <td>{formatDate(demande.ordreMissionDateDebut)} - {formatDate(demande.ordreMissionDateFin)}</td>
                                <td>
                                    {renderLocation(demande) ? (
                                        <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
                                            <MapPin size={13} /> {renderLocation(demande)}
                                        </span>
                                    ) : "-"}
                                </td>
                                <td><span className="pcPill is-on">{demande.status}</span></td>
                                <td className="is-right">
                                    {demande.status === "APPROVED" && (
                                        <>
                                            <button className="pcIconBtn" type="button" title="Télécharger l'ordre de mission PDF" onClick={() => handleDownloadPdf(demande)} disabled={loading}>
                                                <Download size={16} />
                                            </button>
                                            <button className="pcIconBtn" type="button" title="Imprimer l'ordre de mission" onClick={() => handleDownloadPdf(demande, true)} disabled={loading}>
                                                <Printer size={16} />
                                            </button>
                                        </>
                                    )}
                                    {canEditDemande(demande) && (
                                        <button className="pcIconBtn" type="button" title="Modifier" onClick={() => openEdit(demande)} disabled={loading}>
                                            <Edit size={16} />
                                        </button>
                                    )}
                                    {canCancelDemande(demande) && (
                                        <button className="pcIconBtn" type="button" title="Annuler" onClick={() => handleCancel(demande)} disabled={loading}>
                                            <AlertCircle size={16} />
                                        </button>
                                    )}
                                    <button className="pcIconBtn" type="button" title="Dupliquer" onClick={() => handleDuplicate(demande)} disabled={loading}>
                                        <Copy size={16} />
                                    </button>
                                </td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );

    return (
        <div className="obPage">
            <div className="obHeader" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16 }}>
                <div>
                    <h1>Demandes de mission pour mes collaborateurs</h1>
                    <p>Saisissez un ordre de mission et affectez-le a votre equipe.</p>
                </div>
                <button className="obBtn obBtn--primary" type="button" onClick={() => setShowCreateModal(true)}>
                    <Plus size={18} /> Saisir une demande
                </button>
            </div>

            <div className="obWrap">
                {renderCreatedRequests()}
                {showCreateModal && (
                <div className="pcModal">
                    <div className="pcModal__panel pcModal__panel--wide">
                        <div className="pcModal__header">
                            <div className="pcModal__headerLeft">
                                <div className="pcModal__title">Saisir une demande de mission</div>
                                <div className="pcModal__subtitle">Saisissez l'ordre de mission et sélectionnez les collaborateurs concernés.</div>
                            </div>
                            <button className="pcIconBtn pcIconBtn--close" type="button" onClick={() => setShowCreateModal(false)} aria-label="Fermer"><X size={15} /></button>
                        </div>
                        <div className="pcModal__body">
                        <MissionInlineForm
                            formId="managerMissionForm"
                            onSubmit={handleSubmit}
                            loading={loading || directReportsLoading}
                            submitLabel={`Envoyer ${selectedCollaborateurs.length || ""} ordre${selectedCollaborateurs.length > 1 ? "s" : ""}`}
                        >
                            <div className="pcWizStep">
                                <div className="pcWizStep__intro">
                                    <div className="pcWizStep__introIcon"><Users size={20} /></div>
                                    <div>
                                        <div className="pcWizStep__introTitle">Collaborateurs concernés</div>
                                        <div className="pcWizStep__introSub">{selectedCollaborateurs.length} collaborateur(s) sélectionné(s)</div>
                                    </div>
                                </div>

                                <div className="pcWizField is-full">
                                    <label>Rechercher un collaborateur</label>
                                    <div style={{ position: "relative" }}>
                                        <Search size={18} style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "#9ca3af" }} />
                                        <input
                                            className="pcInput"
                                            type="text"
                                            placeholder="Nom, matricule, email..."
                                            value={searchCollaborateur}
                                            onChange={(e) => setSearchCollaborateur(e.target.value)}
                                            style={{ paddingLeft: 40 }}
                                        />
                                    </div>
                                </div>

                                <div style={{ maxHeight: 260, overflowY: "auto", border: "1px solid #e5e7eb", borderRadius: 8, padding: 8 }}>
                                    {directReportsLoading ? (
                                        <div style={{ display: "flex", justifyContent: "center", padding: 20 }}>
                                            <Loader size={24} className="spin" />
                                        </div>
                                    ) : collaborateursFiltres.length === 0 ? (
                                        <div className="pcEmptyCell">Aucun collaborateur trouvé</div>
                                    ) : collaborateursFiltres.map((collab) => (
                                        <label key={collab.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: 10, borderRadius: 6, cursor: "pointer", border: "1px solid #e5e7eb", marginBottom: 6 }}>
                                            <input type="checkbox" checked={selectedCollaborateurs.includes(collab.id)} onChange={() => toggleCollaborateur(collab.id)} />
                                            <div>
                                                <div className="pcStrong">{collab.firstName} {collab.lastName}</div>
                                                <div className="pcMuted" style={{ fontSize: 12 }}>{collab.employeeCode} - {collab.email}</div>
                                            </div>
                                        </label>
                                    ))}
                                </div>
                            </div>
                        </MissionInlineForm>
                    </div>
                    </div>
                </div>
                )}
            </div>

            {showEditModal && selectedDemande && (
                <div className="pcModal">
                    <div className="pcModal__panel pcModal__panel--wide">
                        <div className="pcModal__header">
                            <div className="pcModal__headerLeft">
                                <div className="pcModal__title">Modifier l'ordre de mission</div>
                                <div className="pcModal__subtitle">Modification autorisée tant que la demande n'est pas approuvée ou traitée définitivement.</div>
                            </div>
                            <button
                                className="pcIconBtn pcIconBtn--close"
                                onClick={() => { setShowEditModal(false); setSelectedDemande(null); }}
                                aria-label="Fermer"
                            >
                                <X size={15} />
                            </button>
                        </div>
                        <div className="pcModal__body">
                            <MissionInlineForm
                                formId="managerEditMissionForm"
                                onSubmit={handleEditSubmit}
                                loading={loading}
                                submitLabel="Enregistrer les modifications"
                                initialMissionData={buildInitialMission(selectedDemande)}
                                initialUrgent={selectedDemande.urgent}
                                initialMotif={selectedDemande.motif}
                                showNotes={false}
                                showJustificatifs={false}
                                showComment={false}
                            />
                        </div>
                    </div>
                </div>
            )}

            {showDuplicateModal && selectedDemande && (
                <div className="pcModal">
                    <div className="pcModal__panel pcModal__panel--wide">
                        <div className="pcModal__header">
                            <div className="pcModal__headerLeft">
                                <div className="pcModal__title">Dupliquer l'ordre de mission</div>
                                <div className="pcModal__subtitle">Duplication de l'ordre de mission pour {selectedDemande.employeeName}. Modifiez les informations avant d'envoyer la demande.</div>
                            </div>
                            <button
                                className="pcIconBtn pcIconBtn--close"
                                onClick={() => { setShowDuplicateModal(false); setSelectedDemande(null); }}
                                aria-label="Fermer"
                            >
                                <X size={15} />
                            </button>
                        </div>
                        <div className="pcModal__body">
                            <MissionInlineForm
                                formId="managerDuplicateMissionForm"
                                onSubmit={handleDuplicateSubmit}
                                loading={loading}
                                submitLabel="Dupliquer et envoyer l'ordre de mission'"
                                initialMissionData={buildInitialMission(selectedDemande)}
                                initialUrgent={selectedDemande.urgent}
                                initialMotif={selectedDemande.motif}
                                showNotes={false}
                                showJustificatifs={false}
                                showComment={false}
                            />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DemandeMissionManager;
